/**
 * Copy and demo data for the homepage (unfoldwin.com/). Everything here is static:
 * the homepage has no session, so it never calls the API. Section components read
 * from this file so the copy lives in one place.
 */

export type InputType = "link" | "text";

/**
 * Anti-widow: joins the last two words with a non-breaking space so fixed homepage copy
 * never ends on a single stranded word. Pairs with `text-wrap: balance/pretty` in landing.css.
 */
export const nw = (text: string) => text.replace(/ (?=\S+$)/, "\u00A0");

/** Hero category switcher. Each one only changes the placeholder and the Link/Text mode. */
export const HERO_CATEGORIES: { label: string; placeholder: string; mode: InputType }[] = [
  { label: "Podcasts", placeholder: "Paste a podcast from Spotify or Apple Podcasts…", mode: "link" },
  { label: "Videos", placeholder: "Paste a YouTube link you keep thinking about…", mode: "link" },
  { label: "Articles", placeholder: "Paste an article or Substack post…", mode: "link" },
  { label: "Thoughts", placeholder: "Or just type the thought it left behind…", mode: "text" },
];

/** Index of the "Thoughts" category, which is the only one that uses Text mode. */
export const THOUGHTS_CATEGORY = 3;

/** Real product categories (backend `LIBRARY_TAGS`), limited to the ones the curated cards use. */
export const TRENDING_FILTERS = ["Finance", "Self-care", "Self-growth"] as const;
export type TrendingCategory = (typeof TRENDING_FILTERS)[number];

export interface TrendingCard {
  title: string;
  category: TrendingCategory;
  image: string;
}

const COVER = (name: string) => `/images/landing/covers/${name}.webp`;

/** Curated Trending row. No unfolding counts: a static page has no real ones to show. */
export const TRENDING_CARDS: TrendingCard[] = [
  { title: nw("Why Detaching Is the Only Way to Get What You Want"), category: "Self-growth", image: COVER("detaching") },
  { title: nw("Become Articulate and Speak Smartly"), category: "Self-growth", image: COVER("articulate") },
  { title: nw("Become Your Own Case Study"), category: "Self-growth", image: COVER("case-study") },
  { title: nw("Having Standards Will Change Your Life"), category: "Self-care", image: COVER("standards") },
  { title: nw("8 Tiny Habits to Become Financially Literate"), category: "Finance", image: COVER("financially-literate") },
];

/** Neutral stand-in for a pasted link in the marketing demos. Not a real video. */
export const DEMO_SOURCE = "youtube.com/watch?v=your-link";

/** What the engineered AI helps people do: cycled in the pill on the Better Questions visual. */
export const MIND_OUTCOMES = [
  { text: "Breaking the “Fake Progress” Trap", tone: "lavender" },
  { text: "Action Items Made for Your Reality", tone: "sage" },
  { text: "Turning Small Actions Into Everyday Habits", tone: "butter" },
] as const;

export const TRIGGERS = [
  { line1: "Reframe", line2: "this for my life", tone: "lavender" },
  { line1: "Find", line2: "actionable takeaways", tone: "sage" },
  { line1: "Challenge", line2: "my assumptions", tone: "butter" },
] as const;

/** The question each trigger opens with in the "Unfold it" demo step. */
export const TRIGGER_QUESTIONS = [
  nw("Where are you already making progress — just not counting it?"),
  nw("What is one small win you could lock in before Friday?"),
  nw("Who decided growth has to feel big to count?"),
];

export const DEMO_STEPS = [
  { title: nw("Drop it in"), desc: nw("A link, or the thought it left behind.") },
  { title: nw("Unfold it"), desc: nw("Pick a trigger. Answer in your own words.") },
  { title: "Reflect", desc: nw("Your thoughts, stitched into a journal page.") },
  { title: "WIN", desc: nw("Your next steps, gathered month by month.") },
];

/** The journal page that builds itself in the "Reflect" demo step. */
export const REFLECT_PAGE = {
  date: "Thursday · Sep 26",
  title: nw("Progress I wasn’t counting"),
  answer: nw("I’m already making progress — I just wasn’t counting the small wins."),
  stickies: [
    { label: "Core insight", text: nw("Small wins count."), tone: "butter" },
    { label: "What surfaced", text: nw("I measure myself too harshly."), tone: "sage" },
  ],
};

/** This month's next steps, rising out of the month's folder in the "Your monthly win" demo. */
export const MONTH_TASKS = [
  "Swap afternoon coffee for water",
  "Note how you feel at 4pm Friday",
  "Re-read this entry on Sunday",
];

/** Month folders in the demo, in the Momentum page's own pastel pairs (back, front). */
export const MONTH_FOLDERS = [
  { month: "July", back: "#9dbfb1", front: "#c8ddd4", done: 3, total: 3 },
  { month: "August", back: "#c9bce0", front: "#e2d9f1", done: 2, total: 3 },
] as const;
export const CURRENT_FOLDER = { month: "September", back: "#e8c98e", front: "#f3e3c3" } as const;

export const FAQS = [
  {
    q: nw("Is this just ChatGPT with a pretty UI?"),
    a: nw("No. UnfoldWin is not a chat. We engineered our AI for one job: to ask the questions that get you further, then turn your answers into a written Unfolding with next steps you can act on. You choose the direction. We build the momentum."),
  },
  {
    q: nw('What is an "Unfolding"?'),
    a: nw("One completed reflection, started from a link, a thought you type, or a trigger from the library. It ends when your Unfolding is generated and saved to your journal."),
  },
  {
    q: nw("Is my journal private?"),
    a: nw("Yes. Your Unfoldings are private to you and are never used to train AI models. You can export or delete everything at any time."),
  },
];

/** Same destinations as the shared site footer. */
export const FOOTER_PRODUCT_LINKS = [
  { label: "Library", href: "/library" },
  { label: "Unfoldings", href: "/unfoldings" },
  { label: "Pricing", href: "/pricing" },
  { label: "New Unfolding", href: "/app" },
];

export const FOOTER_COMPANY_LINKS = [
  { label: "Contact", href: "/contact" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
];

export const SOCIAL_LINKS = [
  { label: "Instagram", href: "https://instagram.com/unfoldwin" },
  { label: "Pinterest", href: "https://pinterest.com/unfoldwin" },
  { label: "LinkedIn", href: "https://linkedin.com/company/unfoldwin" },
  { label: "Substack", href: "https://unfoldwin.substack.com" },
] as const;

/** Section anchors. `features`, `explore` and `faq` back the /features, /explore and /faq routes. */
export const NAV_LINKS = [
  { label: "How It Works", href: "/features" },
  { label: "Explore", href: "/explore" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ", href: "/faq" },
];
